import React from 'react';
import cx from 'classnames';

import Popover, { PopoverProps, Alignment } from '~/App/Components/Popover';
import DropdownMenu from '~/App/Components/DropdownMenu';
import { TagPill } from './TagPill';
import { Tag, TaggableResourcePage } from '~/types/models/tags';
import useAnalytics from '~/react-hooks/use-analytics';
import { TAG_EDIT_VIEW } from '~/generated/AnalyticsTypes';

export interface TagPopoverProps extends Partial<Pick<PopoverProps, 'align' | 'preferPlace'>> {
  children: React.ReactNode;
  tagList: Tag[];
  open: boolean;
  sourcePage: TaggableResourcePage;
  onClose(): void;
  onEditClick?(): void;
  onTagSelect?(tag: Tag): void;
  onTagRemove?(tag: Tag): void;
}

const maxVisibleTags = 7;

export function TagPopover({
  children,
  tagList,
  open,
  onClose,
  onEditClick,
  onTagSelect,
  onTagRemove,
  align = Alignment.Right,
  preferPlace = 'below',
  sourcePage
}: TagPopoverProps) {
  const analytics = useAnalytics();

  const handleEditClick = () => {
    analytics.track(TAG_EDIT_VIEW, {
      context: sourcePage
    });
    onEditClick?.();
  };

  const popoverBody = (
    <DropdownMenu>
      <div className='p-2.5'>
        <strong className='text-neutral-800'>Add or remove tags</strong>
        <div className={cx(
          'flex flex-col gap-y-[10px] my-2.5 max-h-[200px] overflow-auto',
          { 'pr-2.5 scrollbar-thin scrollbar-track-neutral-200 scrollbar-thumb-neutral-400': tagList.length > maxVisibleTags }
        )}>
          {tagList.map(({ uuid, name, color, selected_at }) => {
            const tag: Tag = {
              uuid,
              name,
              color,
            };
            const selected = selected_at !== undefined;
            return (
              <TagPill
                className='self-start'
                key={uuid}
                label={name}
                bgColor={color}
                selected={selected}
                onClick={() => !selected && onTagSelect?.(tag)}
                onDeleteClick={() => onTagRemove?.(tag)}
              />
            );
          })}
        </div>
        <div className='text-center'>
          <span className='text-xs text-turquoise mx-auto' role='button' onClick={handleEditClick}>Create & edit tags</span>
        </div>
      </div>
    </DropdownMenu>
  );

  return (
    <Popover
      className='custom'
      align={align}
      preferPlace={preferPlace}
      isOpen={open}
      onOuterAction={onClose}
      body={popoverBody}
      tipSize={0.1}
    >{children}</Popover>
  );
}
